import { encodeFunctionData, encodePacked, parseUnits } from "viem";
import { quoterV2Abi, factoryAbi, poolAbi, routerAbi, FEE_TIERS, ADDRESS_THIS } from "./abis";

export const NATIVE = "native";

export const baseTokens = (cfg) => {
  const c = cfg.contracts;
  const list = [
    { address: NATIVE, symbol: "ETH", name: "Ether (native gas token)", decimals: 18, verified: true },
    { address: c.WETH9, symbol: "WETH", name: "Wrapped Ether", decimals: 18, verified: true },
    { address: c.USDG, symbol: "USDG", name: "Global Dollar", decimals: 6, verified: true },
  ];
  if (cfg.hive_token) list.splice(1, 0, { address: cfg.hive_token, symbol: "HIVE", name: "HIVE", decimals: 18, verified: true, hive: true });
  return list;
};

export const routeAddr = (t, cfg) => (t.address === NATIVE ? cfg.contracts.WETH9 : t.address);
const eq = (a, b) => a.toLowerCase() === b.toLowerCase();

const candidates = (tIn, tOut, cfg) => {
  const routes = FEE_TIERS.map((fee) => ({ tokens: [tIn, tOut], poolFees: [fee] }));
  for (const mid of [cfg.contracts.WETH9, cfg.contracts.USDG]) {
    if (eq(mid, tIn) || eq(mid, tOut)) continue;
    for (const f1 of FEE_TIERS) for (const f2 of FEE_TIERS) routes.push({ tokens: [tIn, mid, tOut], poolFees: [f1, f2] });
  }
  return routes;
};

const encodePath = (r) => r.poolFees.length === 1 ? null
  : encodePacked(["address", "uint24", "address", "uint24", "address"], [r.tokens[0], r.poolFees[0], r.tokens[1], r.poolFees[1], r.tokens[2]]);

const quoteCall = (cfg, r, amt) => r.poolFees.length === 1
  ? { address: cfg.contracts.QuoterV2, abi: quoterV2Abi, functionName: "quoteExactInputSingle", args: [{ tokenIn: r.tokens[0], tokenOut: r.tokens[1], amountIn: amt, fee: r.poolFees[0], sqrtPriceLimitX96: 0n }] }
  : { address: cfg.contracts.QuoterV2, abi: quoterV2Abi, functionName: "quoteExactInput", args: [encodePath(r), amt] };

async function spotRate(pc, cfg, a, b, fee) {
  const pool = await pc.readContract({ address: cfg.contracts.UniswapV3Factory, abi: factoryAbi, functionName: "getPool", args: [a, b, fee] });
  const [slot0, token0] = await Promise.all([
    pc.readContract({ address: pool, abi: poolAbi, functionName: "slot0" }),
    pc.readContract({ address: pool, abi: poolAbi, functionName: "token0" }),
  ]);
  const sp = Number(slot0[0]) / 2 ** 96;
  const p01 = sp * sp;
  return (eq(token0, a) ? p01 : 1 / p01) * (1 - fee / 1e6);
}

export async function getQuote({ pc, cfg, tokenIn, tokenOut, amountIn }) {
  const tIn = routeAddr(tokenIn, cfg);
  const tOut = routeAddr(tokenOut, cfg);
  if (eq(tIn, tOut)) throw new Error("ETH ↔ WETH wrapping is not routed through the DEX");
  const amt = parseUnits(amountIn, tokenIn.decimals);
  if (amt <= 0n) return null;
  const routes = candidates(tIn, tOut, cfg);
  const res = await pc.multicall({ contracts: routes.map((r) => quoteCall(cfg, r, amt)), allowFailure: true });
  let best = null;
  res.forEach((x, i) => {
    if (x.status !== "success") return;
    const out = x.result[0];
    if (out > 0n && (!best || out > best.amountOut)) best = { ...routes[i], amountOut: out, gasEstimate: x.result[3] };
  });
  if (!best) throw new Error("No Uniswap v3 route with liquidity for this pair on Robinhood Chain");
  const [rates, gasPrice] = await Promise.all([
    Promise.all(best.poolFees.map((f, i) => spotRate(pc, cfg, best.tokens[i], best.tokens[i + 1], f))),
    pc.getGasPrice(),
  ]);
  const ideal = Number(amt) * rates.reduce((a, b) => a * b, 1);
  const priceImpact = Math.max(0, 1 - Number(best.amountOut) / ideal) * 100;
  const gasUnits = best.gasEstimate + 60000n * BigInt(best.poolFees.length);
  return { ...best, fee: best.poolFees[0], amountIn: amt, priceImpact, gasUnits, gasPrice, gasWei: gasUnits * gasPrice, at: Date.now() };
}

export const routeLabel = (q, symbolOf) => q.poolFees.length === 1
  ? `Uniswap v3 · ${q.poolFees[0] / 10000}% pool`
  : `Uniswap v3 · via ${symbolOf(q.tokens[1])} (${q.poolFees.map((f) => `${f / 10000}%`).join(" → ")})`;

export function buildSwap({ cfg, tokenIn, tokenOut, quote, slippagePct, account, deadlineMin = 20 }) {
  const minOut = (quote.amountOut * BigInt(10000 - Math.round(slippagePct * 100))) / 10000n;
  const outNative = tokenOut.address === NATIVE;
  const recipient = outNative ? ADDRESS_THIS : account;
  const swapCall = quote.poolFees.length === 1
    ? encodeFunctionData({ abi: routerAbi, functionName: "exactInputSingle", args: [{ tokenIn: quote.tokens[0], tokenOut: quote.tokens[1], fee: quote.poolFees[0],
      recipient, amountIn: quote.amountIn, amountOutMinimum: minOut, sqrtPriceLimitX96: 0n }] })
    : encodeFunctionData({ abi: routerAbi, functionName: "exactInput", args: [{ path: encodePath(quote), recipient, amountIn: quote.amountIn, amountOutMinimum: minOut }] });
  const calls = [swapCall];
  if (outNative) calls.push(encodeFunctionData({ abi: routerAbi, functionName: "unwrapWETH9", args: [minOut, account] }));
  const deadline = BigInt(Math.floor(Date.now() / 1000) + deadlineMin * 60);
  return {
    minOut,
    request: { address: cfg.contracts.SwapRouter02, abi: routerAbi, functionName: "multicall", args: [deadline, calls],
      value: tokenIn.address === NATIVE ? quote.amountIn : 0n },
  };
}
